import { useApolloClient } from '@apollo/client';
import { Button, Icon, Modal, ModalFooter, ModalHeader, ModalMain, Popover, Spinner } from '@sixfold/common-ui';
import { DateTime } from 'luxon';
import React, { useState, useMemo, useCallback } from 'react';

import { transformTourToTimelineData, VehicleTimelineInput } from './data_transformer';
import {
  EtaChart,
  EtaAccuracyChart,
  transformEtaMessagesToTimeSeries,
  ETA_COLORS,
  ETA_ACCURACY_COLORS,
} from './eta_chart';
import { Timeline, useTimelineColors } from './timeline';
import type { StopEtaTimeSeries } from './types';
import { ZoomControl, ZoomState, DEFAULT_ZOOM_STATE, createZoomedTimeScale } from './zoom_control';
import { kafkaMessagesQuery } from '../../../components/bigquery/common/graphql';
import type { Tour } from '../../entities';

import styles from './timeline.module.css';

interface TimelineModalProps {
  tour: Tour;
  vehicles?: VehicleTimelineInput[];
  open: boolean;
  onClose: () => void;
}

export const TimelineModal: React.FC<TimelineModalProps> = ({ tour, vehicles = [], open, onClose }) => {
  const { colors, vehicleColors } = useTimelineColors();
  const timelineData = transformTourToTimelineData(tour, vehicles);
  const client = useApolloClient();

  const [etaLoading, setEtaLoading] = useState(false);
  const [etaData, setEtaData] = useState<StopEtaTimeSeries[]>([]);
  const [etaLoadError, setEtaLoadError] = useState<string | null>(null);

  const [zoomState, setZoomState] = useState<ZoomState>(DEFAULT_ZOOM_STATE);

  const chartWidth = 1600;
  const chartMargin = { top: 20, right: 160, bottom: 50, left: 250 };
  const innerWidth = chartWidth - chartMargin.left - chartMargin.right;

  const fullTimeDomain = useMemo((): [Date, Date] | null => {
    if (!timelineData) return null;

    const allTimes = timelineData.stops.flatMap((stop) => {
      const times: Date[] = [];
      if (stop.timeslot) {
        times.push(new Date(stop.timeslot.begin));
        times.push(new Date(stop.timeslot.end));
      }
      if (stop.arrival_time) {
        times.push(new Date(stop.arrival_time));
      }
      if (stop.departure_time) {
        times.push(new Date(stop.departure_time));
      }
      stop.events.forEach((event) => {
        if (event.event_time) {
          times.push(new Date(event.event_time));
        }
      });
      stop.externalEvents.forEach((event) => {
        times.push(new Date(event.timestamp));
      });
      return times;
    });

    timelineData.tourEvents.forEach((event) => {
      if (event.event_time) {
        allTimes.push(new Date(event.event_time));
      }
    });

    timelineData.tourExternalEvents.forEach((event) => {
      allTimes.push(new Date(event.timestamp));
    });

    etaData.forEach((stopEta) => {
      stopEta.dataPoints.forEach((dp) => {
        allTimes.push(dp.timestamp);
      });
    });

    if (allTimes.length === 0) return null;

    const minTime = Math.min(...allTimes.map((d) => d.getTime()));
    const maxTime = Math.max(...allTimes.map((d) => d.getTime()));

    const padding = (maxTime - minTime) * 0.02;
    return [new Date(minTime - padding), new Date(maxTime + padding)];
  }, [timelineData, etaData]);

  const zoomedTimeScale = useMemo(() => {
    if (!fullTimeDomain) return null;
    return createZoomedTimeScale(fullTimeDomain, zoomState, innerWidth);
  }, [fullTimeDomain, zoomState, innerWidth]);

  const handleZoomChange = useCallback((newState: ZoomState) => {
    setZoomState(newState);
  }, []);

  const handleChartWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      if (!fullTimeDomain) return;

      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - rect.left - chartMargin.left;
      const mouseRatio = Math.max(0, Math.min(1, mouseX / innerWidth));

      const zoomFactor = e.deltaY > 0 ? 1.1 : 0.9;

      const minViewportRatio = 50 / innerWidth;

      const currentWidth = zoomState.viewportEnd - zoomState.viewportStart;
      const newWidth = Math.max(minViewportRatio, Math.min(1, currentWidth * zoomFactor));

      const mouseInViewport = (mouseRatio - zoomState.viewportStart) / currentWidth;
      let newStart = mouseRatio - mouseInViewport * newWidth;
      let newEnd = newStart + newWidth;

      if (newStart < 0) {
        newStart = 0;
        newEnd = newWidth;
      }
      if (newEnd > 1) {
        newEnd = 1;
        newStart = 1 - newWidth;
      }

      setZoomState({ viewportStart: newStart, viewportEnd: newEnd });
      e.preventDefault();
    },
    [fullTimeDomain, zoomState, innerWidth, chartMargin.left],
  );

  const etaQueryTimeRange = useMemo(() => {
    if (!timelineData) return null;

    const allTimes = timelineData.stops.flatMap((stop) => {
      const times: Date[] = [];
      if (stop.timeslot) {
        times.push(new Date(stop.timeslot.begin));
        times.push(new Date(stop.timeslot.end));
      }
      if (stop.arrival_time) {
        times.push(new Date(stop.arrival_time));
      }
      if (stop.departure_time) {
        times.push(new Date(stop.departure_time));
      }
      return times;
    });

    timelineData.tourEvents.forEach((event) => {
      if (event.event_time) {
        allTimes.push(new Date(event.event_time));
      }
    });

    timelineData.tourExternalEvents.forEach((event) => {
      allTimes.push(new Date(event.timestamp));
    });

    if (allTimes.length === 0) return null;

    const minTime = Math.min(...allTimes.map((d) => d.getTime()));
    const maxTime = Math.max(...allTimes.map((d) => d.getTime()));

    const fromDate = DateTime.fromMillis(minTime).minus({ hours: 12 }).startOf('day');
    const toDate = DateTime.fromMillis(maxTime).plus({ hours: 12 }).endOf('day');

    return { fromDate, toDate };
  }, [timelineData]);

  const loadEtaData = useCallback(async () => {
    if (!etaQueryTimeRange) return;

    setEtaLoading(true);
    setEtaLoadError(null);

    try {
      const result = await client.query({
        query: kafkaMessagesQuery,
        variables: {
          keys: [tour.tour_id.toString()],
          messageTypes: ['tour_eta.TourEtaV3'],
          from: etaQueryTimeRange.fromDate.toISO(),
          to: etaQueryTimeRange.toDate.toISO(),
          topics: ['tour-eta'],
        },
      });

      const messages = result.data.bigQueryMessages || [];

      if (timelineData) {
        const transformedData = transformEtaMessagesToTimeSeries(messages, timelineData.stops);
        setEtaData(transformedData);
      }
    } catch (error) {
      console.error('Failed to load ETA data:', error);
      setEtaLoadError(error instanceof Error ? error.message : 'Failed to load ETA data');
    } finally {
      setEtaLoading(false);
    }
  }, [client, tour.tour_id, etaQueryTimeRange, timelineData]);

  const timeslotLegend = [
    { label: 'Loading', color: colors.timeslot.loading },
    { label: 'Unloading', color: colors.timeslot.unloading },
    { label: 'Waypoint', color: colors.timeslot.waypoint },
    { label: 'Other', color: colors.timeslot.other },
  ];

  const eventLegend = [
    {
      label: 'Arrival / Start',
      type: 'circle',
      fill: colors.actual.arrival,
      stroke: '#2E7D32',
    },
    {
      label: 'Departure / Complete',
      type: 'circle',
      fill: colors.actual.departure,
      stroke: '#C62828',
    },
    {
      label: 'Other Event',
      type: 'circle',
      fill: '#BDBDBD',
      stroke: '#9E9E9E',
    },
    {
      label: 'External Arrival',
      type: 'rect',
      fill: colors.actual.arrival,
      stroke: '#2E7D32',
    },
    {
      label: 'External Departure',
      type: 'rect',
      fill: colors.actual.departure,
      stroke: '#C62828',
    },
    {
      label: 'External Other',
      type: 'rect',
      fill: '#BDBDBD',
      stroke: '#9E9E9E',
    },
  ];

  const vehicleLegend = [
    {
      label: 'Allocation Event',
      render: (
        <svg width="16" height="16">
          <polygon
            points="8,2 14,8 8,14 2,8"
            fill={vehicleColors.allocation}
            stroke={vehicleColors.allocationBorder}
            strokeWidth="1"
          />
        </svg>
      ),
    },
    {
      label: 'No Tracking',
      color: vehicleColors.noTracking,
    },
    {
      label: 'Vehicle Near Stop',
      color: vehicleColors.nearStop,
      border: vehicleColors.nearStopBorder,
    },
    {
      label: 'Location Shared',
      color: vehicleColors.locationChunk,
      border: vehicleColors.locationChunkBorder,
    },
    {
      label: 'Breaks',
      color: vehicleColors.break,
      border: vehicleColors.breakBorder,
    },
    {
      label: 'Rest Break',
      color: vehicleColors.restBreak,
      border: vehicleColors.restBreakBorder,
    },
    {
      label: 'Weekend Break',
      color: vehicleColors.weekendBreak,
      border: vehicleColors.weekendBreakBorder,
    },
  ];

  if (!timelineData) {
    return (
      <Modal
        trigger={null}
        open={open}
        onToggle={(nextOpen) => {
          if (!nextOpen) onClose();
        }}
        size="large">
        <ModalHeader title="Tour Timeline" />
        <ModalMain>
          <p>No timeline data available for this tour. This may be because the tour has no stops.</p>
        </ModalMain>
        <ModalFooter>
          <Button onClick={onClose}>Close</Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal
      className={`timeline-modal ${styles.timelineModal}`}
      trigger={null}
      open={open}
      onToggle={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
      size="xlarge">
      <ModalHeader className={styles.modalHeader}>
        <div className={styles.modalHeaderContent}>
          <span>Tour Timeline</span>
          <div className={styles.modalHeaderButtons}>
            <Popover
              placement="bottom-end"
              content={
                <div className={styles.legendContainer}>
                  <h4 className={styles.legendSectionTitleFirst}>Timeslots</h4>
                  <div className={styles.legendItems}>
                    {timeslotLegend.map((item) => (
                      <div key={item.label} className={styles.legendItem}>
                        <div className={styles.legendColorBox} style={{ backgroundColor: item.color }} />
                        <span className={styles.legendLabel}>{item.label}</span>
                      </div>
                    ))}
                  </div>

                  <h4 className={styles.legendSectionTitle}>Events</h4>
                  <div className={styles.legendItemsNarrow}>
                    {eventLegend.map((item) => (
                      <div key={item.label} className={styles.legendItem}>
                        <svg width="16" height="16">
                          {item.type === 'circle' ? (
                            <circle cx="8" cy="8" r="4" fill={item.fill} stroke={item.stroke} strokeWidth={1.5} />
                          ) : (
                            <rect
                              x="4"
                              y="4"
                              width="8"
                              height="8"
                              fill={item.fill}
                              stroke={item.stroke}
                              strokeWidth={1.5}
                              rx="1"
                            />
                          )}
                        </svg>
                        <span className={styles.legendLabel}>{item.label}</span>
                      </div>
                    ))}
                    <div className={styles.legendItem}>
                      <svg width="24" height="10.5" viewBox="0 0 24 10.5" aria-hidden>
                        <line
                          x1="1"
                          y1="5.25"
                          x2="23"
                          y2="5.25"
                          stroke="#FBC02D"
                          strokeWidth={2.5}
                          strokeLinecap="round"
                          opacity={0.9}
                        />
                        <polygon
                          points="-5,-4 5,0 -5,4"
                          fill="#FBC02D"
                          opacity={0.95}
                          transform="translate(12, 5.25)"
                        />
                      </svg>
                      <span className={styles.legendLabel}>Travel between stops</span>
                    </div>
                    <div className={styles.legendItem}>
                      <svg width="21" height="10.5" viewBox="0 0 21 10.5" aria-hidden>
                        <line
                          x1="1"
                          y1="5.25"
                          x2="20"
                          y2="5.25"
                          stroke="#9E9E9E"
                          strokeWidth={2}
                          strokeLinecap="round"
                          opacity={0.9}
                        />
                      </svg>
                      <span className={styles.legendLabel}>Break at stop</span>
                    </div>
                    <div className={styles.legendItem}>
                      <svg width="21" height="10.5" viewBox="0 0 21 10.5" aria-hidden>
                        <line
                          x1="1"
                          y1="3.75"
                          x2="20"
                          y2="3.75"
                          stroke="#CFCFCF"
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          opacity={0.8}
                        />
                        <line
                          x1="1"
                          y1="6.75"
                          x2="20"
                          y2="6.75"
                          stroke="#CFCFCF"
                          strokeWidth={1.5}
                          strokeLinecap="round"
                          opacity={0.8}
                        />
                      </svg>
                      <span className={styles.legendLabel}>Single event</span>
                    </div>
                  </div>

                  {vehicles.length > 0 && (
                    <>
                      <h4 className={styles.legendSectionTitle}>Vehicle Timeline</h4>
                      <div className={styles.legendItemsNarrow}>
                        {vehicleLegend.map((item) => (
                          <div key={item.label} className={styles.legendItem}>
                            {item.render ?? (
                              <div
                                className={styles.legendColorBoxSmall}
                                style={{
                                  backgroundColor: item.color,
                                  border: item.border ? `1px solid ${item.border}` : 'none',
                                }}
                              />
                            )}
                            <span className={styles.legendLabel}>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {etaData.length > 0 && (
                    <>
                      <h4 className={styles.legendSectionTitle}>ETA Chart</h4>
                      <div className={styles.legendItemsNarrow}>
                        <div className={styles.legendItem}>
                          <div className={styles.legendLine} style={{ backgroundColor: ETA_COLORS.arrivalTime }} />
                          <span className={styles.legendLabel}>ETA (arrival_time)</span>
                        </div>
                        <div className={styles.legendItem}>
                          <div className={styles.legendLine} style={{ backgroundColor: ETA_COLORS.unregulatedEta }} />
                          <span className={styles.legendLabel}>Unregulated ETA</span>
                        </div>
                        <div className={styles.legendItem}>
                          <div
                            className={styles.legendLine}
                            style={{ backgroundColor: ETA_COLORS.actualArrival, opacity: 0.7 }}
                          />
                          <span className={styles.legendLabel}>Actual Arrival (ATA)</span>
                        </div>
                      </div>

                      <h4 className={styles.legendSectionTitle}>ETA Accuracy Chart</h4>
                      <div className={styles.legendItemsNarrow}>
                        <div className={styles.legendItem}>
                          <div
                            className={styles.legendLine}
                            style={{ backgroundColor: ETA_ACCURACY_COLORS.errorLine }}
                          />
                          <span className={styles.legendLabel}>ETA Error (ETA - ATA)</span>
                        </div>
                        <div className={styles.legendItem}>
                          <div
                            className={styles.legendLine}
                            style={{ backgroundColor: ETA_ACCURACY_COLORS.zeroLine }}
                          />
                          <span className={styles.legendLabel}>Zero Error Line</span>
                        </div>
                      </div>
                      <p
                        className={styles.legendLabel}
                        style={{ color: 'var(--color-neutral-text-subtle)', margin: '0 0 8px 0' }}>
                        Negative error = optimistic (arrived later). Positive error = pessimistic (arrived earlier). MAE
                        = Mean Absolute Error, Bias = average signed error.
                      </p>

                      <p
                        className={styles.legendLabel}
                        style={{ color: 'var(--color-neutral-text-subtle)', margin: '0 0 8px 0' }}>
                        Different line styles (solid, dashed) represent different stops. Use the checkboxes to filter
                        stops.
                      </p>
                    </>
                  )}

                  <div className={styles.legendFooter}>
                    <p className={styles.legendFooterText}>
                      Click a row to expand and view event details. All times are in UTC.
                    </p>
                  </div>
                </div>
              }>
              <Button kind="subtle" size="small" iconStart="small-info" style={{ fontSize: '12px' }}>
                Legend
              </Button>
            </Popover>
            <Popover
              placement="bottom-end"
              content={
                <div className={styles.feedbackContent}>
                  <p style={{ marginBottom: '8px' }}>
                    <strong>Have feedback or suggestions?</strong>
                  </p>
                  <p style={{ marginBottom: '8px' }}>
                    Write to <strong>#feat-timeline</strong> channel in Visibility Slack or contact Core team
                  </p>
                </div>
              }>
              <Button
                kind="primary"
                size="small"
                iconStart="small-send"
                style={{
                  background: 'linear-gradient(135deg, #f59e0b 0%, #ea580c 100%)',
                  fontSize: '12px',
                  fontWeight: 600,
                  boxShadow: '0 2px 8px rgba(245, 158, 11, 0.4)',
                }}>
                Feedback
              </Button>
            </Popover>
          </div>
        </div>
      </ModalHeader>
      <ModalMain className={styles.modalMain}>
        <div className={styles.scrollContainer} onWheel={handleChartWheel}>
          <Timeline
            data={timelineData}
            width={chartWidth}
            margin={chartMargin}
            timeScale={zoomedTimeScale || undefined}
          />

          {/* ETA Data Section */}
          <div className={styles.etaSection}>
            <div className={styles.etaSectionHeader}>
              <h3 className={styles.etaSectionTitle}>ETA History</h3>
              <Button
                kind="primary"
                size="small"
                loading={etaLoading}
                disabled={etaLoading || !etaQueryTimeRange}
                onClick={() => loadEtaData()}
                iconStart="small-circular-clock">
                {etaData.length > 0 ? 'Reload ETA Data' : 'Load ETA Data'}
              </Button>
            </div>

            {etaLoadError && <div className={styles.errorMessage}>Error: {etaLoadError}</div>}

            {etaLoading && (
              <div className={styles.loadingContainer}>
                <Spinner />
                <p className={styles.loadingText}>Loading ETA messages from BigQuery...</p>
              </div>
            )}

            {!etaLoading && etaData.length === 0 && !etaLoadError && (
              <div className={styles.emptyState}>
                <Icon name="small-circular-clock" size={32} className={styles.emptyStateIcon} />
                <p style={{ margin: 0 }}>
                  Click &quot;Load ETA Data&quot; to fetch historical ETA messages and visualize how ETAs evolved over
                  time.
                </p>
              </div>
            )}

            {!etaLoading && etaData.length > 0 && zoomedTimeScale && (
              <EtaChart
                stops={timelineData.stops}
                etaData={etaData}
                timeScale={zoomedTimeScale}
                width={chartWidth}
                margin={{ left: chartMargin.left, right: chartMargin.right }}
              />
            )}
          </div>

          {/* ETA Accuracy Section */}
          {!etaLoading && etaData.length > 0 && zoomedTimeScale && (
            <div className={styles.etaSection}>
              <div className={styles.etaSectionHeader}>
                <h3 className={styles.etaSectionTitle}>ETA Accuracy</h3>
              </div>
              <p style={{ fontSize: '12px', margin: '0 0 12px 0' }}>
                Shows the difference between predicted ETA and actual arrival time over time. Negative values indicate
                optimistic predictions (arrived later than predicted), positive values indicate pessimistic predictions
                (arrived earlier than predicted). Only stops with recorded arrival times are shown.
              </p>
              <EtaAccuracyChart
                etaData={etaData}
                timeScale={zoomedTimeScale}
                width={chartWidth}
                margin={{ left: chartMargin.left, right: chartMargin.right }}
              />
            </div>
          )}
        </div>
      </ModalMain>
      {/* Sticky Zoom Control */}
      {fullTimeDomain && (
        <div className={styles.zoomControlContainer}>
          <ZoomControl
            fullTimeDomain={fullTimeDomain}
            zoomState={zoomState}
            onZoomChange={handleZoomChange}
            width={chartWidth}
            margin={{ left: chartMargin.left, right: chartMargin.right }}
          />
        </div>
      )}
      <ModalFooter className={styles.modalFooterBar}>
        <Button onClick={onClose}>Close</Button>
      </ModalFooter>
    </Modal>
  );
};
